/** Escape text for a vCard 3.0 value (backslash, comma, semicolon, newlines). */
const esc = (value) =>
  String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

/** Fetch an image and return { base64, type } for embedding, or null. */
const fetchImage = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    let blob = await response.blob();
    // vCards can't hold SVG; draw it onto a canvas and embed a PNG instead
    if (blob.type.includes('svg') || /\.svg(\?|$)/i.test(url)) blob = await svgToPng(blob);
    const type = blob.type.includes('png') ? 'PNG' : 'JPEG';
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return { base64, type };
  } catch (error) {
    console.error('Error fetching image for vCard:', error);
    return null;
  }
};

const svgToPng = (svgBlob, maxSize = 512) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([svgBlob], { type: 'image/svg+xml' }));
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || maxSize, h = img.naturalHeight || maxSize;
      const scale = maxSize / Math.max(w, h);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('SVG conversion failed'))), 'image/png');
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('SVG could not be drawn')); };
    img.src = url;
  });

/**
 * Build a vCard 3.0 file for a card.
 * @param {object} card    row from public.cards
 * @param {object} [theme] row from public.brand_themes (for the company logo)
 */
export const generateVCF = async (card, theme) => {
  const parts = (card.full_name || '').trim().split(/\s+/);
  const last = parts.length > 1 ? parts.pop() : '';
  const first = parts.join(' ');

  const lines = ['BEGIN:VCARD', 'VERSION:3.0', `FN:${esc(card.full_name)}`, `N:${esc(last)};${esc(first)};;;`];
  if (card.title) lines.push(`TITLE:${esc(card.title)}`);
  if (card.company || card.department) lines.push(`ORG:${esc(card.company)}${card.department ? `;${esc(card.department)}` : ''}`);
  if (card.email) lines.push(`EMAIL;TYPE=INTERNET,WORK:${esc(card.email)}`);
  if (card.phone) lines.push(`TEL;TYPE=WORK,VOICE:${esc(card.phone)}`);
  if (card.website) lines.push(`URL:${esc(card.website)}`);
  if (card.linkedin) lines.push(`X-SOCIALPROFILE;TYPE=linkedin:${esc(card.linkedin)}`);
  if (card.twitter) lines.push(`X-SOCIALPROFILE;TYPE=twitter:${esc(card.twitter)}`);
  if (card.instagram) lines.push(`X-SOCIALPROFILE;TYPE=instagram:${esc(card.instagram)}`);

  if (card.photo_url) {
    const photo = await fetchImage(card.photo_url);
    if (photo) lines.push(`PHOTO;ENCODING=b;TYPE=${photo.type}:${photo.base64}`);
  }
  if (theme?.logo_url) {
    const logo = await fetchImage(theme.logo_url);
    if (logo) lines.push(`LOGO;ENCODING=b;TYPE=${logo.type}:${logo.base64}`);
  }

  lines.push('END:VCARD');
  return lines.join('\r\n');
};

export const downloadVCF = async (card, theme) => {
  const vcf = await generateVCF(card, theme);
  const blob = new Blob([vcf], { type: 'text/vcard;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(card.full_name || 'contact').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\w\- ]+/g, '').trim() || 'contact'}.vcf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
